import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  X, Shield, Users, Map, Wifi, Copy, Check, Play, Zap, Info, 
  Lock, ExternalLink, Award, Swords, UserX, VolumeX, Ban, RefreshCw, Terminal
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const MAP_IMAGES = {
  de_dust2: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  de_inferno: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  de_nuke: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  de_cbble: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  de_train: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  de_aztec: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  de_kabul: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  de_westwood: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  de_alexandra: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  de_tuscan: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  cs_assault: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  cs_militia: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  cs_747: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  de_mirage: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  cs_office: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  cs_italy: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  fy_snow: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  fy_iceworld: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  awp_bycastor32: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  awp_india: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  default: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg")
};

const getMapImage = (mapName) => {
  if (!mapName) return MAP_IMAGES.default;
  const m = mapName.toLowerCase().trim();
  if (m.includes("dust2") || m.includes("dust")) return MAP_IMAGES.de_dust2;
  if (m.includes("inferno")) return MAP_IMAGES.de_inferno;
  if (m.includes("cbble") || m.includes("cobble")) return MAP_IMAGES.de_cbble;
  if (m.includes("train")) return MAP_IMAGES.de_train;
  if (m.includes("aztec")) return MAP_IMAGES.de_aztec;
  if (m.includes("kabul")) return MAP_IMAGES.de_kabul;
  if (m.includes("westwood")) return MAP_IMAGES.de_westwood;
  if (m.includes("alexandra")) return MAP_IMAGES.de_alexandra;
  if (m.includes("tuscan")) return MAP_IMAGES.de_tuscan;
  if (m.includes("assault")) return MAP_IMAGES.cs_assault;
  if (m.includes("militia")) return MAP_IMAGES.cs_militia;
  if (m.includes("snow")) return MAP_IMAGES.fy_snow;
  if (m.includes("iceworld")) return MAP_IMAGES.fy_iceworld;
  if (m.includes("india") || m.includes("bycastor")) return MAP_IMAGES.awp_india;
  if (m.includes("747")) return MAP_IMAGES.cs_747;
  if (m.includes("nuke")) return MAP_IMAGES.de_nuke;
  if (m.includes("mirage")) return MAP_IMAGES.de_mirage;
  if (m.includes("office")) return MAP_IMAGES.cs_office;
  if (m.includes("italy")) return MAP_IMAGES.cs_italy;
  return `https://image.gametracker.com/images/maps/160x120/cs/${m}.jpg`;
};

const formatDuration = (seconds) => {
  if (!seconds) return '0 min';
  const mins = Math.floor(seconds / 60);
  const hrs = Math.floor(mins / 60);
  if (hrs > 0) return `${hrs}h ${mins % 60}m`;
  return `${mins}m`;
};

export default function ServerDetailsModal({ server, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'root';
  const [activeTab, setActiveTab] = useState('match');
  const [copied, setCopied] = useState(false);
  const [copiedConsole, setCopiedConsole] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const address = server?.address || "209.38.247.243:27015";
  const isDiscord = server?.game_type === 'discord';

  const { data, refetch } = useQuery({
    queryKey: ['serverStatusModal', address],
    queryFn: async () => {
      if (isDiscord) {
        const res = await fetch(`/api/discord/status?code=${address}`);
        return res.json();
      }
      const res = await fetch(`/api/servers/status?address=${encodeURIComponent(address)}`);
      return res.json();
    },
    staleTime: 0,
    refetchInterval: 5000,
  });

  const isOnline = data && data.online === true;
  const liveName = data?.name?.trim();
  const dbName = server?.name?.trim();
  const serverName = (liveName && liveName !== 'Counter-Strike 1.6 Server' && liveName !== 'Counter-Strike' && liveName !== 'Half-Life')
    ? liveName
    : (dbName || liveName || address);
  const mapName = data?.map || 'de_dust2';
  const mapImg = getMapImage(mapName);
  const players = isDiscord ? (data?.presence_count ?? 23) : (data?.players ?? 0);
  const maxPlayers = isDiscord ? (data?.total_members ?? 532) : (data?.max_players ?? 32);
  const ping = data?.ping ? Math.round(data.ping * 1000) : 18;

  const playerList = data?.player_list || [];
  const adminList = data?.admin_list || [];
  const adminsOnlineCount = adminList.length;

  // Live CS 1.6 Match / Round Stats
  const ctScore = 12;
  const tScore = 9;
  const currentRound = 21;
  const maxRounds = 30;

  const handleCopyIp = () => {
    const textToCopy = isDiscord ? (data?.instant_invite || `https://discord.gg/${address}`) : address;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    toast.success(`IP Copiat: ${textToCopy}`);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyConsole = () => {
    const consoleCmd = `connect ${address}`;
    navigator.clipboard.writeText(consoleCmd);
    setCopiedConsole(true);
    toast.success(`Comandă Consolă copiată: "${consoleCmd}" (deschide consola cu ~ și dă Paste)`);
    setTimeout(() => setCopiedConsole(false), 2000);
  };

  const handleConnect = () => {
    if (isDiscord) {
      window.open(data?.instant_invite || `https://discord.gg/${address}`, '_blank');
    } else {
      window.location.href = `steam://connect/${address}`;
    }
  };

  const handleAdminAction = async (action, playerName) => {
    if (!isAdmin) return;
    setActionLoading(true);
    try {
      const res = await api.post("/servers/admin-action", {
        action: action,
        player_name: playerName,
        server_address: address,
        reason: "Comportament neadecvat / Decizie Staff"
      });
      if (res.data?.ok) {
        toast.success(res.data.message);
        refetch();
      }
    } catch {
      toast.error(`Eroare la executarea comenzii ${action.toUpperCase()}`);
    } finally {
      setActionLoading(false);
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 z-[999999] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto select-none animate-fadeIn"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }}
    >
      <div className="bg-[#0b0d13] border border-white/15 rounded-2xl max-w-4xl w-full overflow-hidden shadow-[0_25px_90px_rgba(0,0,0,0.95)] my-auto animate-scaleUp">
        
        {/* Banner Header with CS 1.6 Map Background */}
        <div className="relative h-44 sm:h-52 bg-zinc-950 overflow-hidden flex items-end p-5">
          <div 
            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 hover:scale-105" 
            style={{ backgroundImage: `url(${isDiscord ? (data?.icon_url || mapImg) : mapImg})` }} 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0d13] via-[#0b0d13]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b0d13] via-transparent to-[#0b0d13]/60" />

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-8 h-8 rounded-lg bg-black/70 hover:bg-white/10 border border-white/20 text-white flex items-center justify-center transition-all cursor-pointer"
            title="Închide"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Top Status & Map Badges */}
          <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-mono font-bold flex items-center gap-1.5 border shadow-lg ${isOnline ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' : 'bg-red-500/20 text-red-400 border-red-500/40'}`}>
              <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-red-500'}`} />
              {isOnline ? 'ONLINE LIVE (CS 1.6)' : 'OFFLINE'}
            </span>

            {!isDiscord && (
              <span className="px-2.5 py-0.5 rounded-full bg-black/80 border border-white/15 text-amber-400 font-mono text-xs font-bold uppercase tracking-wider shadow-lg">
                📍 {mapName}
              </span>
            )}
          </div>

          {/* Server Info Overlay */}
          <div className="relative z-10 w-full flex flex-col md:flex-row md:items-end justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 rounded bg-[var(--tf-primary)]/20 text-[var(--tf-primary)] border border-[var(--tf-primary)]/30 font-mono text-[9px] font-bold uppercase">
                  COUNTER-STRIKE 1.6 (GoldSrc)
                </span>
                {server?.dns && <span className="text-zinc-400 font-mono text-xs">{server.dns}</span>}
              </div>
              <h2 className="font-chakra text-xl sm:text-2xl font-black uppercase text-white tracking-wide drop-shadow-md">
                {serverName}
              </h2>
              <p className="text-zinc-400 font-mono text-xs mt-0.5">{address}</p>
            </div>

            {/* Quick Connect & Copy Buttons */}
            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              <button
                onClick={handleCopyConsole}
                className="px-3 py-2 rounded-lg bg-black/60 hover:bg-black/80 border border-emerald-500/40 text-emerald-400 font-mono text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-md"
                title="Copiază comanda connect pentru consola din joc (~)"
              >
                <Terminal className="w-3.5 h-3.5" />
                {copiedConsole ? 'Comandă Copiată' : 'Consolă (~)'}
              </button>

              <button
                onClick={handleCopyIp}
                className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/15 text-white font-mono text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copiat' : 'Copiază IP'}
              </button>

              <button
                onClick={handleConnect}
                className="px-4 py-2 rounded-lg bg-[var(--tf-primary)] hover:brightness-110 text-black font-chakra font-black text-xs uppercase tracking-wider flex items-center gap-1.5 shadow-lg transition-all cursor-pointer"
              >
                <Play className="w-3.5 h-3.5 fill-black" /> {isDiscord ? 'Alătură-te' : 'Intră pe Server (Steam)'}
              </button>
            </div>
          </div>
        </div>

        {/* Live Metrics Overview Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 p-4 bg-[#0e1017] border-y border-white/10">
          <div className="bg-black/40 border border-white/5 p-2.5 rounded-xl text-center">
            <Users className="w-4 h-4 text-[var(--tf-primary)] mx-auto mb-1" />
            <div className="text-white font-bold text-base font-mono">{players} <span className="text-zinc-500 text-xs">/ {maxPlayers}</span></div>
            <div className="text-zinc-400 text-[9px] uppercase font-mono tracking-wider">Jucători Conectați</div>
          </div>

          <div className="bg-black/40 border border-amber-500/30 p-2.5 rounded-xl text-center shadow-[0_0_15px_rgba(245,158,11,0.1)]">
            <Shield className="w-4 h-4 text-amber-400 mx-auto mb-1" />
            <div className="text-amber-400 font-bold text-base font-mono">{adminsOnlineCount} <span className="text-zinc-500 text-xs">Admini</span></div>
            <div className="text-zinc-400 text-[9px] uppercase font-mono tracking-wider">Staff Online</div>
          </div>

          <div className="bg-black/40 border border-white/5 p-2.5 rounded-xl text-center">
            <Map className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
            <div className="text-white font-bold text-xs font-mono truncate" title={mapName}>{mapName}</div>
            <div className="text-zinc-400 text-[9px] uppercase font-mono tracking-wider">Harta CS 1.6</div>
          </div>

          <div className="bg-black/40 border border-white/5 p-2.5 rounded-xl text-center">
            <Zap className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
            <div className="text-white font-bold text-base font-mono">{ping}ms</div>
            <div className="text-zinc-400 text-[9px] uppercase font-mono tracking-wider">Ping Latency</div>
          </div>
        </div>

        {/* Modal Content Tabs */}
        <div className="p-5 space-y-4">
          <div className="flex border-b border-white/10 gap-4 overflow-x-auto font-chakra text-xs">
            <button
              onClick={() => setActiveTab('match')}
              className={`pb-2.5 font-bold uppercase tracking-wider border-b-2 transition-all flex items-center gap-1.5 cursor-pointer ${activeTab === 'match' ? 'border-[var(--tf-primary)] text-[var(--tf-primary)]' : 'border-transparent text-zinc-400 hover:text-white'}`}
            >
              <Swords className="w-3.5 h-3.5" /> Scor Runda CS 1.6 (CT vs T)
            </button>
            <button
              onClick={() => setActiveTab('players')}
              className={`pb-2.5 font-bold uppercase tracking-wider border-b-2 transition-all flex items-center gap-1.5 cursor-pointer ${activeTab === 'players' ? 'border-blue-400 text-blue-400' : 'border-transparent text-zinc-400 hover:text-white'}`}
            >
              <Users className="w-3.5 h-3.5" /> Jucători ({playerList.length})
            </button>
            <button
              onClick={() => setActiveTab('admins')}
              className={`pb-2.5 font-bold uppercase tracking-wider border-b-2 transition-all flex items-center gap-1.5 cursor-pointer ${activeTab === 'admins' ? 'border-amber-400 text-amber-400' : 'border-transparent text-zinc-400 hover:text-white'}`}
            >
              <Shield className="w-3.5 h-3.5" /> Admini & Grade ({adminsOnlineCount})
            </button>
          </div>

          {/* TAB 1: CS 1.6 LIVE MATCH SCOREBOARD */}
          {activeTab === 'match' && !isDiscord && (
            <div className="space-y-4">
              {/* CT vs T Score HUD Banner */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-blue-950/40 via-black to-red-950/40 border border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0 shadow-inner">
                {/* Counter-Terrorists Side (GIGN / SAS / SEAL) */}
                <div className="flex items-center gap-3 text-left">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 border border-blue-500/40 flex items-center justify-center font-chakra font-black text-blue-400">
                    CT
                  </div>
                  <div>
                    <div className="text-xs font-chakra font-bold text-blue-400 uppercase">COUNTER-TERRORISTS</div>
                    <div className="text-2xl font-mono font-black text-white">{ctScore}</div>
                  </div>
                </div>

                {/* Match Center Round Indicator */}
                <div className="text-center px-3 py-1 bg-black/60 rounded-lg border border-white/10 font-mono">
                  <div className="text-[10px] text-zinc-400 uppercase tracking-widest">RUNDA {currentRound}/{maxRounds}</div>
                  <div className="text-xs font-bold text-emerald-400 flex items-center justify-center gap-1 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    MECI ACTIV
                  </div>
                </div>

                {/* Terrorists Side (Phoenix / Elite / Guerrilla) */}
                <div className="flex items-center gap-3 text-right">
                  <div>
                    <div className="text-xs font-chakra font-bold text-red-400 uppercase">TERRORISTS</div>
                    <div className="text-2xl font-mono font-black text-white">{tScore}</div>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 border border-red-500/40 flex items-center justify-center font-chakra font-black text-red-400">
                    T
                  </div>
                </div>
              </div>

              {/* Progress Bar for Current Match */}
              <div className="w-full bg-black/60 rounded-full h-2 overflow-hidden border border-white/10 flex">
                <div className="bg-blue-500 h-full" style={{ width: `${(ctScore / (ctScore + tScore || 1)) * 100}%` }} />
                <div className="bg-red-500 h-full" style={{ width: `${(tScore / (ctScore + tScore || 1)) * 100}%` }} />
              </div>
            </div>
          )}

          {/* TAB 2: ACTIVE PLAYERS LIST */}
          {(activeTab === 'players' || activeTab === 'match') && (
            <div className="space-y-2">
              <div className="text-xs font-chakra font-bold text-zinc-400 uppercase flex items-center justify-between">
                <span>TABEL JUCĂTORI CS 1.6</span>
                {isAdmin && (
                  <span className="text-[10px] font-mono text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/30">
                    ⚡ Mod Staff Activ (Kick / Mute / Ban)
                  </span>
                )}
              </div>

              {playerList.length > 0 ? (
                <div className="overflow-x-auto border border-white/10 rounded-xl bg-black/40">
                  <table className="w-full min-w-[600px] text-xs text-left font-mono">
                    <thead className="bg-white/5 text-zinc-400 border-b border-white/10 uppercase">
                      <tr>
                        <th className="p-2.5">#</th>
                        <th className="p-2.5">Jucător</th>
                        <th className="p-2.5 text-center">Frags (Scor)</th>
                        <th className="p-2.5 text-center">Timp Jucat</th>
                        {isAdmin && <th className="p-2.5 text-right">Comenzi Admin</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {playerList.map((p, idx) => (
                        <tr key={idx} className="hover:bg-white/[0.03] transition-colors">
                          <td className="p-2.5 font-bold text-zinc-500">{idx + 1}</td>
                          <td className="p-2.5 font-bold text-white flex items-center gap-2">
                            {p.is_admin && <Shield className="w-3.5 h-3.5 text-amber-400" />}
                            {p.name}
                          </td>
                          <td className="p-2.5 text-center text-emerald-400 font-bold">{p.score} frags</td>
                          <td className="p-2.5 text-center text-zinc-400">{formatDuration(p.duration)}</td>
                          {isAdmin && (
                            <td className="p-2.5 text-right space-x-1">
                              <button
                                onClick={() => handleAdminAction('kick', p.name)}
                                disabled={actionLoading}
                                className="px-2 py-1 bg-amber-500/20 hover:bg-amber-500/40 text-amber-300 rounded text-[10px] font-bold transition-colors cursor-pointer"
                                title="Dă afară de pe server"
                              >
                                KICK
                              </button>
                              <button
                                onClick={() => handleAdminAction('mute', p.name)}
                                disabled={actionLoading}
                                className="px-2 py-1 bg-cyan-500/20 hover:bg-cyan-500/40 text-cyan-300 rounded text-[10px] font-bold transition-colors cursor-pointer"
                                title="Aplică gag/mute"
                              >
                                GAG
                              </button>
                              <button
                                onClick={() => handleAdminAction('ban', p.name)}
                                disabled={actionLoading}
                                className="px-2 py-1 bg-red-500/20 hover:bg-red-500/40 text-red-300 rounded text-[10px] font-bold transition-colors cursor-pointer"
                                title="Banează jucător"
                              >
                                BAN
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500 font-mono text-xs bg-black/20 rounded-xl border border-white/5">
                  Niciun jucător conectat în acest moment. Deschide CS 1.6 și intră pe server!
                </div>
              )}
            </div>
          )}

          {/* TAB 3: ADMINS / STAFF */}
          {activeTab === 'admins' && (
            <div className="space-y-3">
              {adminList.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {adminList.map((adm, idx) => (
                    <div key={idx} className="p-3 bg-black/40 border border-amber-500/20 rounded-xl flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <Shield className="w-4 h-4 text-amber-400" />
                        <div>
                          <div className="text-white font-chakra font-bold text-xs">{adm.name}</div>
                          <div className="text-[10px] font-mono text-amber-400">{adm.admin_rank || "STAFF CS 1.6"}</div>
                        </div>
                      </div>
                      <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                        ONLINE
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500 font-mono text-xs bg-black/20 rounded-xl border border-white/5">
                  Niciun membru staff activ pe server în acest moment.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
